/**
 * Conversational review panel for one card. Drives the multi-turn
 * Opus conversation:
 *
 *   1. POST /review/start → resume or create session, get transcript
 *   2. POST /review/stream → SSE stream of Opus turns; parses events:
 *        assistant_text       — Opus thinking / explanation
 *        clarification        — Opus asks a question; user replies
 *        schema_extension     — Opus registered a new optional field
 *        validation_failure   — emit_final_abilities failed; loop continues
 *        finalized            — accepted; card leaves queue
 *        error                — terminal failure
 *   3. On clarification, show input; on submit, fire another stream
 *      call with the user's message.
 *
 * Persistent transcripts live in D1 (card_review_sessions); reload
 * the page and the conversation resumes from where it left off.
 */

import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { apiPath } from '../api.js';

type TranscriptEntry = {
  readonly role: 'user' | 'assistant' | 'tool';
  readonly content: string;
  readonly timestamp: number;
  readonly tool?: { readonly name: string; readonly input: unknown };
};

type StartResponse = {
  ok: boolean;
  session_id?: string;
  status?: 'open' | 'awaiting_user' | 'finalized' | 'abandoned';
  transcript?: readonly TranscriptEntry[];
  error?: string;
};

type StreamEvent =
  | { kind: 'assistant_text'; text: string }
  | { kind: 'clarification'; question: string; tool_use_id: string }
  | {
      kind: 'schema_extension';
      extension_id: string;
      field_name: string;
      ok: boolean;
      message: string;
    }
  | { kind: 'validation_failure'; reason: string }
  | { kind: 'finalized'; abilities: unknown }
  | { kind: 'error'; message: string };

type Props = {
  readonly cardId: string;
  readonly onFinalized: () => void;
};

export function ReviewChat({ cardId, onFinalized }: Props): ReactElement {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<readonly TranscriptEntry[]>([]);
  const [streaming, setStreaming] = useState<boolean>(false);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [reply, setReply] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [extensions, setExtensions] = useState<
    readonly { extension_id: string; field_name: string; message: string }[]
  >([]);
  const startedRef = useRef<boolean>(false);

  // Append synthesized assistant_text events into the transcript view.
  // Persistence lives server-side; this is just for live render.
  const appendLive = useCallback((entry: TranscriptEntry) => {
    setTranscript((t) => [...t, entry]);
  }, []);

  const runStream = useCallback(
    async (currentSessionId: string, message: string) => {
      setStreaming(true);
      setErrorMsg(null);
      const handleEvent = (evt: StreamEvent): void => {
        switch (evt.kind) {
          case 'assistant_text':
            appendLive({
              role: 'assistant',
              content: evt.text,
              timestamp: Math.floor(Date.now() / 1000),
            });
            return;
          case 'clarification':
            setPendingQuestion(evt.question);
            appendLive({
              role: 'tool',
              content: evt.question,
              timestamp: Math.floor(Date.now() / 1000),
              tool: { name: 'ask_clarification', input: { question: evt.question } },
            });
            return;
          case 'schema_extension':
            setExtensions((xs) => [
              ...xs,
              { extension_id: evt.extension_id, field_name: evt.field_name, message: evt.message },
            ]);
            return;
          case 'validation_failure':
            appendLive({
              role: 'tool',
              content: `Validation failed: ${evt.reason}`,
              timestamp: Math.floor(Date.now() / 1000),
              tool: { name: 'validation_failure', input: {} },
            });
            return;
          case 'finalized':
            appendLive({
              role: 'tool',
              content: 'Accepted ✓',
              timestamp: Math.floor(Date.now() / 1000),
              tool: { name: 'emit_final_abilities', input: {} },
            });
            onFinalized();
            return;
          case 'error':
            setErrorMsg(evt.message);
            return;
        }
      };
      try {
        const res = await fetch(apiPath(`/api/admin/needs-review/${cardId}/review/stream`), {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ session_id: currentSessionId, message }),
        });
        if (!res.body) {
          setErrorMsg('No response stream from server.');
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          // SSE: events separated by blank lines.
          const events = buf.split('\n\n');
          buf = events.pop() ?? '';
          for (const ev of events) {
            const evt = parseSSEEvent(ev);
            if (!evt) continue;
            handleEvent(evt);
          }
        }
      } catch (e) {
        setErrorMsg(e instanceof Error ? e.message : String(e));
      } finally {
        setStreaming(false);
      }
    },
    [cardId, appendLive, onFinalized],
  );

  // Boot the session on mount.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void (async () => {
      try {
        const res = await fetch(apiPath(`/api/admin/needs-review/${cardId}/review/start`), {
          method: 'POST',
          credentials: 'include',
        });
        const body = (await res.json()) as StartResponse;
        if (!body.ok || !body.session_id) {
          setErrorMsg(body.error ?? 'failed to start session');
          return;
        }
        setSessionId(body.session_id);
        setTranscript(body.transcript ?? []);
        // If we resumed an awaiting_user session, the last tool entry
        // is the pending question; surface it.
        const last = (body.transcript ?? []).at(-1);
        if (last?.role === 'tool' && last.tool?.name === 'ask_clarification') {
          setPendingQuestion(last.content);
        } else if (body.status !== 'finalized') {
          // Open session: drive the first turn.
          await runStream(body.session_id, '');
        }
      } catch (e) {
        setErrorMsg(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [cardId, runStream]);

  const submitReply = useCallback(() => {
    if (!sessionId || !reply.trim()) return;
    setPendingQuestion(null);
    appendLive({
      role: 'user',
      content: reply,
      timestamp: Math.floor(Date.now() / 1000),
    });
    const message = reply;
    setReply('');
    void runStream(sessionId, message);
  }, [sessionId, reply, runStream, appendLive]);

  const abandon = useCallback(async () => {
    await fetch(apiPath(`/api/admin/needs-review/${cardId}/review/abandon`), {
      method: 'POST',
      credentials: 'include',
    });
    setTranscript([]);
    setPendingQuestion(null);
    setSessionId(null);
    startedRef.current = false;
  }, [cardId]);

  return (
    <div className="space-y-2">
      <div className="max-h-96 overflow-y-auto rounded border border-cosmos-700 bg-cosmos-950 p-2 text-xs">
        {transcript.length === 0 && !streaming ? (
          <p className="text-cosmos-500">Starting conversation…</p>
        ) : null}
        {transcript.map((e, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: transcript is append-only, so position is a stable identifier within the live render.
            key={`${e.timestamp}-${e.role}-${i}`}
            className={`mb-1.5 ${
              e.role === 'user'
                ? 'text-emerald-300'
                : e.role === 'tool'
                  ? 'text-amber-300'
                  : 'text-cosmos-100'
            }`}
          >
            <span className="font-mono text-[10px] uppercase text-cosmos-500">
              {e.role === 'tool' ? `tool · ${e.tool?.name ?? '?'}` : e.role}
            </span>
            <pre className="mt-0.5 whitespace-pre-wrap font-sans">{e.content}</pre>
          </div>
        ))}
        {streaming ? <p className="text-cosmos-500 italic">…thinking</p> : null}
      </div>

      {extensions.length > 0 ? (
        <div className="rounded border border-violet-700 bg-violet-950/40 p-2 text-violet-200 text-xs">
          <strong className="text-violet-300">Schema extensions registered:</strong>
          <ul className="mt-1 list-disc pl-4">
            {extensions.map((x) => (
              <li key={x.extension_id}>
                <code className="text-violet-100">{x.field_name}</code> — {x.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {errorMsg ? (
        <div className="rounded border border-rose-700 bg-rose-950/40 p-2 text-rose-200 text-xs">
          {errorMsg}
        </div>
      ) : null}

      {pendingQuestion ? (
        <div className="space-y-2 rounded border border-amber-700 bg-amber-950/30 p-2">
          <p className="text-amber-200 text-xs">
            <strong>Opus asks:</strong> {pendingQuestion}
          </p>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Your answer…"
            rows={3}
            className="w-full rounded border border-amber-600 bg-cosmos-950 p-2 text-cosmos-100 text-xs focus:border-amber-400 focus:outline-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                submitReply();
              }
            }}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => void abandon()}
              className="rounded bg-cosmos-700 px-2 py-1 text-cosmos-200 text-xs hover:bg-cosmos-600"
            >
              Abandon session
            </button>
            <button
              type="button"
              onClick={submitReply}
              disabled={!reply.trim() || streaming}
              className="rounded bg-amber-600 px-3 py-1 font-semibold text-cosmos-950 text-xs hover:bg-amber-500 disabled:opacity-50"
            >
              Reply (⌘↵)
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Parse one SSE block (`event: name\ndata: {...}`) into a typed event. */
function parseSSEEvent(block: string): StreamEvent | null {
  const lines = block.split('\n');
  let event = '';
  let data = '';
  for (const line of lines) {
    if (line.startsWith('event: ')) event = line.slice(7).trim();
    else if (line.startsWith('data: ')) data += line.slice(6);
  }
  if (!event || !data) return null;
  try {
    const parsed = JSON.parse(data);
    return { kind: event as StreamEvent['kind'], ...parsed } as StreamEvent;
  } catch {
    return null;
  }
}
